// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCreateMenu, type GlobalCreateMenuProps} from '@github-ui/global-create-menu'
import {GlobalUserNavDrawer, type GlobalUserNavDrawerProps} from '@github-ui/global-user-nav-drawer'
import {AccountSwitchDialog, type AccountSwitchDialogProps} from '@github-ui/global-user-nav-drawer/AccountSwitchDialog'
import {GitPullRequestIcon, IssueOpenedIcon, RepoIcon} from '@primer/octicons-react'
import {Stack} from '@primer/react'
import {clsx} from 'clsx'

import {
  type GlobalNavMenuItem,
  InternallyAnchoredGlobalNavMenu,
  type InternallyAnchoredGlobalNavMenuProps,
  type WithShow,
} from '../menu/GlobalNavMenu'
import {ItemDivider} from '../primitives/ItemDivider'
import {NavLinkItem} from '../primitives/NavItemLink'
import {TopNav, TopNavProvider} from '../primitives/TopNav'
import {GlobalNavAnalyticsProvider} from '../utils/Analytics'
import {HideOnError} from '../utils/HideOnError'
import styles from './GlobalNavBar.module.css'
import type {ContextRegionPayload} from './items/ContextRegion'
import {ContextRegion, useRegisterContextRegion} from './items/ContextRegion'
import {CopilotItems, type CopilotItemsProps} from './items/CopilotItems'
import {EnterpriseBanner, type EnterpriseBannerProps} from './items/EnterpriseBanner'
import {HeaderLogo, type HeaderLogoProps} from './items/HeaderLogo'
import {NotificationItem, type NotificationItemProps} from './items/Notification'
import {CommandPalette, MobileSearch, Search, type SearchProps} from './items/Search'
import {LocalNavigation} from './LocalNavigation'

type LinkItemProps = Partial<WithShow<Pick<GlobalNavMenuItem, 'href' | 'hotkey'>>>

export interface GlobalNavBarProps {
  /** Props to control the left navigation menu */
  navMenu: InternallyAnchoredGlobalNavMenuProps

  /** Props to control the right, user menu */
  userMenu: GlobalUserNavDrawerProps

  /** Props for the confirmation dialog that appears after switching accounts */
  accountSwitchDialog?: Omit<AccountSwitchDialogProps, 'owner' | 'switchAccountPath'>

  /** Information about the user's notifications */
  notifications?: NotificationItemProps

  /** Props for the main header logo */
  headerLogo?: HeaderLogoProps

  /** Props for the "Create New..." menu. */
  createMenu?: GlobalCreateMenuProps

  /** Config options for the issues menu bar link */
  issues?: LinkItemProps

  /** Config options for the pull requests menu bar link */
  pulls?: LinkItemProps

  /** Config options for the contributed repositories menu bar link */
  contributedRepos?: LinkItemProps

  /** Context region data with breadcrumb navigation */
  contextRegion?: ContextRegionPayload

  /** Config options for the copilot menu and buttons */
  copilot?: CopilotItemsProps

  /** Props for the enterprise banner shown above the nav bar */
  enterpriseBanner?: EnterpriseBannerProps

  /** Props for the search buttons */
  search?: SearchProps & {
    /** Path to fetch search fragments from */
    fragmentsPath?: string
    /** Additional query params to include when fetching search fragments */
    fragmentsParams?: string
  }
}

export function GlobalNavBar(props: GlobalNavBarProps) {
  const {
    navMenu,
    userMenu,
    headerLogo,
    createMenu,
    copilot,
    notifications,
    contextRegion,
    search,
    enterpriseBanner,
    accountSwitchDialog,
    issues,
    pulls,
    contributedRepos,
  } = props
  const hasLocalNavigation = !!contextRegion?.localNavigation && contextRegion.localNavigation.length > 0
  const contextRegionProps = useRegisterContextRegion()

  return (
    <GlobalNavAnalyticsProvider category="GLOBAL_NAV_BAR">
      <TopNavProvider
        hasLocalNavigation={hasLocalNavigation}
        isSearchPage={props.search?.isSearchPage ?? false}
        isJumpToSearch={props.search?.isJumpToSearch ?? false}
        searchFragmentsPath={search?.fragmentsPath}
        searchFragmentsParams={search?.fragmentsParams}
      >
        <TopNav>
          <TopNav.Above>
            <EnterpriseBanner {...enterpriseBanner} />
          </TopNav.Above>
          <TopNav.Left>
            <HideOnError>
              <InternallyAnchoredGlobalNavMenu {...navMenu} />
            </HideOnError>
            <HeaderLogo {...headerLogo} />
          </TopNav.Left>

          <TopNav.Center className={clsx(!contextRegion?.crumbs?.length && styles.noBreadcrumbs)}>
            <HideOnError>
              <ContextRegion {...contextRegionProps} crumbs={contextRegion?.crumbs} />
            </HideOnError>
            <HideOnError>
              <Search {...search} />
            </HideOnError>
          </TopNav.Center>

          <TopNav.Right>
            <Stack
              data-testid="top-bar-actions"
              direction="horizontal"
              align="center"
              gap="condensed"
              padding="none"
              className="hide-sm hide-md"
            >
              <HideOnError>
                <CopilotItems {...copilot} />
              </HideOnError>

              <ItemDivider />
              {createMenu && (
                <HideOnError>
                  <GlobalCreateMenu {...createMenu} />
                </HideOnError>
              )}

              <GlobalNavBarLinkItems issues={issues} pulls={pulls} contributedRepos={contributedRepos} />
            </Stack>
            <NotificationItem {...notifications} />
            <HideOnError>
              <GlobalUserNavDrawer {...userMenu} />
            </HideOnError>
            {accountSwitchDialog && (
              <AccountSwitchDialog
                {...accountSwitchDialog}
                owner={userMenu.owner}
                switchAccountPath={userMenu.switchAccountPath}
              />
            )}
          </TopNav.Right>

          <MobileSearch {...search} />

          {hasLocalNavigation && (
            <HideOnError>
              <LocalNavigation tabs={contextRegion.localNavigation} selectedLink={contextRegion.selectedLink} />
            </HideOnError>
          )}
        </TopNav>
        <HideOnError>
          <CommandPalette {...search} />
        </HideOnError>
      </TopNavProvider>
    </GlobalNavAnalyticsProvider>
  )
}

function GlobalNavBarLinkItems(props: Pick<GlobalNavBarProps, keyof typeof GlobalNavBarItems>) {
  return (
    <>
      {(['issues', 'pulls', 'contributedRepos'] as const).map(key => {
        const {label, ...navItemProps} = GlobalNavBarItems[key]
        const {show, ...mergedProps} = {...navItemProps, ...props[key]}

        if (show === false) {
          return null
        }

        return <NavLinkItem key={key} {...mergedProps} aria-label={label} />
      })}
    </>
  )
}

/**
 * The items in the global navigation bar
 * Any config set here will be the default, and can be overridden by passing props to the GlobalNavBar component
 */
export const GlobalNavBarItems = {
  issues: {
    href: '/issues',
    icon: IssueOpenedIcon,
    label: 'Issues',
    analyticsKey: 'ISSUES_HEADER',
    show: true,
  },
  pulls: {
    href: '/pulls',
    icon: GitPullRequestIcon,
    label: 'Pull requests',
    analyticsKey: 'PULL_REQUESTS_HEADER',
    show: true,
  },
  contributedRepos: {
    href: '/repos',
    label: 'Repositories',
    analyticsKey: 'REPOS_HEADER',
    icon: RepoIcon,
    show: false,
  },
} as const satisfies Record<string, WithShow<Omit<GlobalNavMenuItem, 'key'>>>

try{ GlobalNavBar.displayName ||= 'GlobalNavBar' } catch {}
try{ GlobalNavBarLinkItems.displayName ||= 'GlobalNavBarLinkItems' } catch {}